/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */

import { ReactNode } from 'react'
import styled from 'styled-components'
import { Button, FlexBox, Icon, Text } from 'vspry-style-components'

import { isDictionaryKey, useLocale } from 'context/localeContext'

import theme from 'styles/theme'

import { isNavItem, isNavSection, NavItem, NavSection, NavState } from 'components/navigation/NavDrawer'

type NavMoreLayoutProps = {
    items: (NavItem | NavSection | NavSection<NavItem | NavSection>)[]
    handleClick: (to?: string, onClick?: (state: NavState) => void) => void
    onClose: () => void
    signOut: () => void
}

const Overlay = styled(FlexBox)`
    position: fixed;
    top: 0px;
    bottom: 0px;
    gap: 20px;
    background: white;
    z-index: 1006;
    flex-direction: column;
    justify-content: flex-start;
    padding: 20px;
    transition: transform 0.3s ease, opacity 0.3s ease;
    opacity: 1;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
`

const IconCircle = styled.div`
    border-radius: 50%;
    height: 50px;
    width: 50px;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`

const MoreMenuItem = styled.div`
    flex: 0 0 45%;
    height: 10vh;
    width: 10px;
    background: transparent;
    border-radius: 15px;
`

export default function MoreOverlay({ items, handleClick, onClose, signOut }: NavMoreLayoutProps) {
    const { translate } = useLocale()

    const itemsExcludedSignOut = items.map((i) =>
        isNavSection(i)
            ? {
                  ...i,
                  items: i.items.filter((child) => child.title !== 'navigation.signOut'),
              }
            : i
    )

    const renderItem = ({ to, onClick, icon, title }: NavItem) => (
        <MoreMenuItem
            onClick={() => {
                handleClick(to, onClick)
                onClose()
            }}
        >
            <FlexBox $column align='center' justify='center'>
                <IconCircle>
                    <Icon name={icon} fitted size='large' />
                </IconCircle>
                <Text size='xSmall'>{isDictionaryKey(title) ? translate(title) : title}</Text>
            </FlexBox>
        </MoreMenuItem>
    )

    const renderSection = ({ items: subitems }: NavSection | NavSection<NavItem | NavSection>): ReactNode =>
        subitems.map((i) => (isNavItem(i) ? renderItem(i) : renderSection(i)))

    return (
        <Overlay>
            <FlexBox $align onClick={onClose}>
                <Text $left $bold size='small' margin='no'>
                    {translate('navigation.More')}
                </Text>
                <Icon name='times' onClick={onClose} />
            </FlexBox>

            <FlexBox $wrap gap='medium'>
                {itemsExcludedSignOut.map((i) => (isNavItem(i) ? renderItem(i) : renderSection(i)))}
            </FlexBox>
            <Button
                id='signout'
                customColor={theme.error}
                onClick={() => {
                    onClose()
                    signOut()
                }}
            >
                {translate('navigation.signOut')} <Icon name='sign out' />
            </Button>
        </Overlay>
    )
}
